import * as CurrentsSelectors from '../current';
import * as GroupsSelectors from '../groups';
import * as CommentSelectors from '../comments';
import * as CollaboratorTypes from './types';

export const getCollaborators = (state = {}) =>
    state.collaborators || {};

export const getCollaboratorIds = (state = {}) =>
    Object.keys(getCollaborators(state));

export const getCollaboratorById = (state = {}, collaboratorId) => {
    const collaborators = getCollaborators(state);
    return collaborators[collaboratorId] || {};
};

export const getCurrentCollaborator = (state = {}) =>
    getCollaboratorById(state, CurrentsSelectors.getCurrentCollaboratorId(state));

export const getCurrentCollaboratorIdByGroupId = (state = {}, groupId) => {
    if (!groupId) {
        return;
    }

    const currentCollaboratorId = CurrentsSelectors.getCurrentCollaboratorId(state);
    const currentUserId = CurrentsSelectors.getCurrentUserId(state);

    if (currentCollaboratorId && !currentUserId) { return currentCollaboratorId; }

    const collaborators = getCollaborators(state);

    const { id } = Object.values(collaborators)
        .find((collaborator) => collaborator.userId === currentUserId && collaborator.groupId === groupId);

    return id;
};

export const getCurrentCollaboratorIdByGroupType = (state = {}, groupType) => {
    const groupId = GroupsSelectors.getGroupIdByType(state, groupType);
    return getCurrentCollaboratorIdByGroupId(state, groupId);
};

export const getCollaboratorsIds = (state = {}) => Object.keys(getCollaborators(state));

export const getCollaboratorIdsByGroupIdAndEmails = (state = {}, groupId, emails = []) =>
    Object.values(getCollaborators(state))
        .filter((collaborator) => collaborator.groupId === groupId)
        .filter((collaborator) => emails.includes(collaborator.email))
        .map(({ id }) => id);

export const getCollaboratorsByGroupId = (state = {}, groupId) => {
    const collaborators = Object.values(getCollaborators(state));
    return collaborators.filter((collaborator) => collaborator.groupId === groupId);
};

export const getCollaboratorsByGroupType = (state = {}, groupType) => {
    const groupId = GroupsSelectors.getGroupIdByType(state, groupType);
    return getCollaboratorsByGroupId(state, groupId);
};

export const isCollaboratorsDataExist = (state = {}) =>
    getCollaboratorsIds(state).length > 0;

export const isPresentByCollaboratorId = (state = {}, collaboratorId) => {
    const { id } = getCollaboratorById(state, collaboratorId);

    return !!id;
};

export const hasUsernameByCollaboratorId = (state = {}, collaboratorId) => {
    const username = getCollaboratorUsernameById(state, collaboratorId);

    return !!username;
};

export const isPresentAndIsWithoutUsernameByCollaboratorId = (state = {}, collaboratorId) => {
    const isPresent = isPresentByCollaboratorId(state, collaboratorId);
    const hasNoUsername = !hasUsernameByCollaboratorId(state, collaboratorId);

    return isPresent && hasNoUsername;
};

export const getCurrentGroupCollaborators = (state = {}) => {
    const currentGroupId = CurrentsSelectors.getCurrentGroupId(state);
    return getCollaboratorsByGroupId(state, currentGroupId);
};

export const getCollaboratorIsRemovedById = (state = {}, collaboratorId) => {
    const { removed } = getCollaboratorById(state, collaboratorId);
    return !!removed;
};

export const getNonRemovedCollaboratorsByGroupType = (state = {}, groupType) => {
    const collaborators = getCollaboratorsByGroupType(state, groupType);
    return collaborators.filter((collaborator) => !getCollaboratorIsRemovedById(state, collaborator.id));
};

export const getNonRemovedCollaboratorsByCurrentGroup = (state = {}) => {
    const currentGroupType = GroupsSelectors.getCurrentGroupType(state);
    return getNonRemovedCollaboratorsByGroupType(state, currentGroupType);
};

export const getActiveParticipatorsByCurrentGroup = (state = {}) => {
    const nonRemovedCollaborators = getNonRemovedCollaboratorsByCurrentGroup(state);
    return nonRemovedCollaborators.filter((collaborator) => isActiveParticipator(state, collaborator));
};

export function isActiveParticipator(state, collaborator) {
    if (getCollaboratorType(collaborator) !== CollaboratorTypes.ANONYMOUS) {
        return true;
    }

    return hasCommented(state, collaborator);
}

const hasCommented = (state, collaborator) => CommentSelectors.getCommentsByCollaboratorId(state, collaborator.id).length > 0;

export const getCollaboratorsWithoutCurrentCollaboratorByGroupType = (state = {}, groupType) => {
    const currentCollaboratorId = getCurrentCollaboratorIdByGroupType(state, groupType);
    const collaborators = getCollaboratorsByGroupType(state, groupType);
    return collaborators.filter((collaborator) => collaborator.id !== currentCollaboratorId);
};

export const getCollaboratorsWithoutCurrentCollaboratorAndNonRemovedByGroupType = (state = {}, groupType) => {
    const currentCollaboratorId = getCurrentCollaboratorIdByGroupType(state, groupType);
    const collaborators = getCollaboratorsByGroupType(state, groupType);
    return collaborators.filter((collaborator) => collaborator.id !== currentCollaboratorId && !getCollaboratorIsRemovedById(state, collaborator.id));
};

export const getCollaboratorUsernameById = (state = {}, id) => {
    const { username } = getCollaboratorById(state, id);
    return username;
};

export const getCollaboratorUserIdById = (state = {}, id) => {
    const { userId } = getCollaboratorById(state, id);
    return userId;
};

export const getCollaboratorEmailById = (state = {}, id) => {
    const { email } = getCollaboratorById(state, id);
    return email;
};

export const getCollaboratorSafeUsernameById = (state = {}, id) =>
    getCollaboratorUsernameById(state, id) ||
    getCollaboratorEmailById(state, id);

export const getCollaboratorUserImageById = (state = {}, id) => {
    const { userImage } = getCollaboratorById(state, id);
    return userImage;
};

export const getCollaboratorFullNameById = (state = {}, id) => {
    const { fullName } = getCollaboratorById(state, id);
    return fullName;
};

export const getCurrentCollaboratorUsername = (state = {}) =>
    getCollaboratorUsernameById(
        state,
        CurrentsSelectors.getCurrentCollaboratorId(state)
    );

export const getCollaboratorIdsByGroupType = (state = {}, groupType, { withoutRemoved = false, withoutCurrent = false, withoutAnonymous = false }) => {
    let collaborators;

    if (withoutRemoved && withoutCurrent) {
        collaborators = getCollaboratorsWithoutCurrentCollaboratorAndNonRemovedByGroupType(state, groupType);
    } else if (withoutRemoved && !withoutCurrent) {
        collaborators = getNonRemovedCollaboratorsByGroupType(state, groupType);
    } else if (!withoutRemoved && withoutCurrent) {
        collaborators = getCollaboratorsWithoutCurrentCollaboratorByGroupType(state, groupType);
    } else if (!withoutRemoved && !withoutCurrent) {
        collaborators = getCollaboratorsByGroupType(state, groupType);
    }
    if (withoutAnonymous) {
        collaborators = findNotAnonymous(collaborators);
    }

    return collaborators.map(({ id }) => id);
};

function findNotAnonymous(collaborators) {
    return collaborators.filter((collaborator) => !isAnonymous(collaborator));
}

function isAnonymous(collaborator) {
    return getCollaboratorType(collaborator) === CollaboratorTypes.ANONYMOUS;
}

const getCollaboratorType = (collaborator) => collaborator.type;

export const getCollaboratorPermissionsById = (state = {}, collaboratorId) => {
    const { permissions } = getCollaboratorById(state, collaboratorId);
    return permissions || {};
};

export const getCurrentCollaboratorPermissions = (state = {}) => {
    const currentCollaboratorId = CurrentsSelectors.getCurrentCollaboratorId(state);
    return getCollaboratorPermissionsById(state, currentCollaboratorId);
};

export const getCollaboratorInvitePermissionsById = (state = {}, collaboratorId) =>
    getCollaboratorPermissionsById(state, collaboratorId).invite;

export const getCurrentCollaboratorInvitePermissions = (state = {}) =>
    !!getCurrentCollaboratorPermissions(state).invite;

export const getCollaboratorFullnameById = (state = {}, collaboratorId) => {
    const collaborator = getCollaboratorById(state, collaboratorId);
    return collaborator.fullName;
};

export const getCurrentCollaboratorFullName = (state = {}) => {
    const currentCollaboratorId = CurrentsSelectors.getCurrentCollaboratorId(state);
    return getCollaboratorFullnameById(state, currentCollaboratorId);
};

export const getCollaboratorEmailNotificationPreferenceById = (state = {}, collaboratorId) => {
    const collaborator = getCollaboratorById(state, collaboratorId);
    return collaborator.emailNotificationEnabled;
};

export const getCurrentCollaboratorEmailNotificationPreference = (state = {}) => {
    const currentCollaboratorId = CurrentsSelectors.getCurrentCollaboratorId(state);
    return getCollaboratorEmailNotificationPreferenceById(state, currentCollaboratorId);
};

export const isEmpty = (state = {}) =>
    getCollaboratorIds(state).length === 0;

export const isCurrentCollaboratorExternal = (state = {}) => !CurrentsSelectors.getCurrentUserId(state);

export const getCollaboratorsWithoutCurrentUsernameByGroupType = (state = {}, groupType) => {
    const collaborators = getCollaboratorsWithoutCurrentCollaboratorByGroupType(state, groupType);
    return collaborators.map((collaborator) => getCollaboratorUsernameById(state, collaborator.id));
};

export const getNotCurrentCollaboratorUsernameInSellerGroup = (state = {}) =>
    getCollaboratorsWithoutCurrentUsernameByGroupType(state, 'seller')[0];

export const getCollaboratorsWithoutCurrenUserIdByGroupType = (state = {}, groupType) => {
    const collaborators = getCollaboratorsWithoutCurrentCollaboratorByGroupType(state, groupType);
    return collaborators.map((collaborator) => getCollaboratorUserIdById(state, collaborator.id));
};

export const getNotCurrentCollaboratorUserIdInSellerGroup = (state = {}) =>
    getCollaboratorsWithoutCurrenUserIdByGroupType(state, 'seller')[0];
